/**
 * StatusGate — top-level wiring of the deploy/update notification UI.
 * iter-156bu.
 *
 * Renders into the React tree below the AppStatusProvider. Reads the
 * context and decides which (if any) modal to show. Also installs the
 * global axios interceptor that translates SESSION_STALE / MAINTENANCE
 * / UPDATE_REQUIRED response codes into modal state.
 *
 * Render order matters: SessionStaleModal > MaintenanceModal >
 * UpdateModal. A user being kicked out due to a bad session takes
 * precedence over "please refresh".
 */
import { useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppStatus } from '@/contexts/AppStatusContext';
import { useAuth } from '@/contexts/AuthContext';
// iter-156ep — `UpdateModal` is no longer rendered from here (we always
// show the soft banner instead). The named export is left registered in
// `StatusModals` so any consumer using it directly still resolves.
import { MaintenanceModal, SessionStaleModal } from '@/components/system/StatusModals';

// Module-level guard so we install the interceptor exactly once across
// React Strict-Mode double-mounts and lazy re-renders.
let _interceptorInstalled = false;

export const StatusGate = () => {
  const status = useAppStatus();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Install the global axios interceptor on first mount.
  useEffect(() => {
    if (_interceptorInstalled) return;
    _interceptorInstalled = true;

    axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        try {
          const r = error?.response;
          if (!r) {
            // Network error → check for chunk failures via the
            // window error handler installed below.
            return Promise.reject(error);
          }
          // Backend uses `detail` (FastAPI default). We embed
          // `{code, message}` for our 3 special signals.
          const detail = r.data?.detail;
          const code =
            (detail && typeof detail === 'object' && detail.code) ||
            r.data?.code ||
            r.headers?.['x-strata-status-code'];

          if (code === 'SESSION_STALE') {
            status.showSessionStale();
          } else if (code === 'MAINTENANCE_MODE') {
            status.showMaintenance();
          } else if (code === 'UPDATE_REQUIRED') {
            status.showUpdateRequired();
          }
        } catch { /* never let our handler crash the request flow */ }
        return Promise.reject(error);
      },
    );
  }, [status]);

  // Catch ChunkLoadError from lazy-loaded routes after a deploy.
  useEffect(() => {
    const onErr = (event) => {
      const msg = String(event?.error?.message || event?.message || '');
      const isChunkErr =
        event?.error?.name === 'ChunkLoadError' ||
        /Loading chunk \d+ failed/i.test(msg) ||
        /failed to fetch dynamically imported module/i.test(msg);
      if (isChunkErr) {
        // First failure → auto-reload once (provider handles
        // the "already tried" guard). Subsequent failures show
        // the modal so the user can click Refresh Now.
        status.requestRefreshOnce();
      }
    };
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', (e) => onErr({ error: e?.reason, message: e?.reason?.message }));
    return () => window.removeEventListener('error', onErr);
  }, [status]);

  // Force-logout: when forceLogout becomes true, clear auth state
  // and navigate to login. We only do this if we actually have a
  // signed-in user — otherwise the SessionStaleModal handles it.
  useEffect(() => {
    if (status.forceLogout && user) {
      // Don't await — logout clears cookies; navigate is non-blocking.
      try { logout?.(); } catch { /* noop — already logged out */ }
      // The SessionStaleModal will display itself; navigation to
      // login happens when the user clicks its button.
    }
  }, [status.forceLogout, user, logout]);

  const handleRefresh = () => {
    // Force a hard reload to bust the bundler chunk cache.
    window.location.reload();
  };
  const handleLogin = () => {
    try { logout?.(); } catch { /* noop — token already cleared */ }
    navigate('/login', { replace: true });
    window.location.reload(); // wipe in-memory state
  };

  const msg = status.appStatus?.message;

  // Precedence: SessionStale > Maintenance > Update.
  if (status.forceLogout) {
    return <SessionStaleModal onLogin={handleLogin} message={msg} />;
  }
  if (status.maintenance) {
    return <MaintenanceModal message={msg} />;
  }
  if (status.needsRefresh) {
    // iter-156ep — ALWAYS soft. Never block the user mid-work with a
    // modal redirect. The autosave layer (iter-156eo) is persisting
    // any draft work in the background; the field tech / office user
    // refreshes when they're at a natural stopping point. Previously
    // a hard `window.location.reload()` was triggered on any deploy
    // OR any ChunkLoadError — that's how the pre-walk data-loss
    // incident happened.
    return (
      <div
        data-testid="status-update-soft-banner"
        className="fixed bottom-4 right-4 z-[60] max-w-sm rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 shadow-lg px-4 py-3"
      >
        <div className="text-sm font-medium text-amber-900 dark:text-amber-200">
          A new version is available
        </div>
        <div className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-0.5 mb-2">
          {msg || (
            <>Your work is being saved automatically. Click Refresh when
            you&apos;re at a good stopping point.</>
          )}
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          data-testid="status-update-soft-refresh"
          className="text-xs font-medium px-3 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white"
        >
          Refresh now
        </button>
      </div>
    );
  }
  return null;
};

export default StatusGate;
